import * as Hogan from 'hogan.js';
import { Rectangle, Container} from 'pixi.js';
import {
    CommandLineInterface,
    consoleLogger,
    CoreDI, inject, Store,
} from '@gongxigames/framework/core';
import {EnvironmentProperty} from '@gongxigames/framework/slot/types';

import {GameOnlyEventMap, slotStore, strictUiEmitter} from '@gongxigames/framework/slotV2/globals';
import {cacheAsSprite} from '@gongxigames/framework/core/fragments/display/CacheAsSprite';
import {$UIText} from '@gongxigames/framework/slotV2/ui/ui-koala/JSX/UIText';
import {getProp, getTexture} from '@gongxigames/framework/core/fragments/BuilderY';
import {ResponsiveHandler} from '@gongxigames/framework/core/fragments/display/responsive';
import {METER_TEXT_FIELD_ID} from '@gongxigames/framework/slotV2/ui/ui-koala/modules/UIMainScreen/components/WinArea/WinMeterManager';
import {coreStore} from '@gongxigames/framework/core/globals';
import {InfoModule} from '@gongxigames/framework/slotV2/modules/info';

export class GameRules<T extends GameOnlyEventMap> extends InfoModule<T>{
    public static readonly NAME: symbol = Symbol('GameRules');

    @inject(CoreDI.EnvironmentProperty)
    protected _environmentProperty!: EnvironmentProperty;

    @inject(CoreDI.CLI)
    protected _cli!: CommandLineInterface;

    protected _gameRulesContainer!: HTMLElement;
    protected _gameRules!: HTMLElement;

    protected _needToLoad = true;
    protected _loaded = false;
    protected _isOpen = false;

    public init(): void {
        super.init();

        this.createPage();

        /* develblock:start */
        this._cli.addCommand('gameRules', {
            show: () => {
                const menuBar = this._cli.getCommand('menuBar');

                // for mobile
                if (menuBar) {
                    if (typeof menuBar !== 'function') {
                        menuBar.showGameRules();
                    }
                } else {
                    this.onPageClicked();
                }
            },
            close: () => {
                this.close();

                const menuBar = this._cli.getCommand('menuBar');

                if (menuBar && typeof menuBar !== 'function') {
                    menuBar.close();
                }
            }
        });
        /* develblock:end */
    }

    protected async generateLocalizedImages(): Promise<void> {
        await this.generateBetImages();
        await this.generateCashImage();
        await this.generateWinImage();
    }

    protected async generateBetImages(): Promise<void> {
        await this.getTextureFromCache('game_rules_bet_display_landscape');
        await this.getTextureFromCache('game_rules_bet_plus_minus_landscape');
        await this.getTextureFromCache('game_rules_bet_select_popup');
        await this.getTextureFromCache('game_rules_bet_select_portrait');
    }

    private getMoneyValues() {
        return Store.computed(() => {
            const currencyFormatter = slotStore.currencyFormatter.value!;
            const balance = getProp(slotStore.slot_ui_label_balance);
            const value = currencyFormatter.formatCents(balance);

            return {value};
        });
    }

    protected async generateCashImage(): Promise<void> {
        const text = $UIText({
            localeID: 'creditPortrait',
            name: 'credit',
            stylePreset: 'BET_VALUE_PORTRAIT',
            values: this.getMoneyValues()
        }) as Container;

        new ResponsiveHandler(text).update();

        cacheAsSprite.add({
            name: 'game_rules_cash_img',
            provider: text,
            region: new Rectangle(-text.width/2, -text.height/2, text.width, text.height),
        });

        await this.getTextureFromCache('game_rules_cash_img');
    }

    protected async generateWinImage(): Promise<void> {
        const text = $UIText({
            localeID: 'winPortrait',
            name: METER_TEXT_FIELD_ID,
            stylePreset: 'WIN_VALUE_LABEL_PORTRAIT',
            values: this.getMoneyValues()
        }) as Container;

        new ResponsiveHandler(text).update();

        cacheAsSprite.add({
            name: 'game_rules_win_img',
            provider: text,
            resolution: 0.5,
            region: new Rectangle(-text.width/2, -text.height/2, text.width, text.height),
        });

        await this.getTextureFromCache('game_rules_win_img');
    }

    protected async getTextureFromCache(key: string): Promise<void> {
        if (getTexture(key)) {
            consoleLogger.error(`Texture already exists: ${key}`);
        }

        coreStore
            .resourceLoader
            .value!
            .setTexture(key, cacheAsSprite.get(key).texture);
    }

    public destroy(): void {
        super.destroy();

        if (this._gameRulesContainer && this._gameRulesContainer.parentElement) {
            this._gameRulesContainer.parentElement.removeChild(this._gameRulesContainer);
        }
    }

    protected onSetupEvents(): void {
        strictUiEmitter.on('UI.menu.gameRulesClicked', this.onPageClicked.bind(this));
        this.emitter.on('Game.command.hideInfo', this.close.bind(this));
    }

    protected createPage(): void {
        this._gameRulesContainer = document.createElement('div');
        this._gameRulesContainer.id = 'gameRulesContainer';
        document.body.appendChild(this._gameRulesContainer);

        this._gameRules = document.createElement('div');
        this._gameRules.id = 'gameRules';
        this._gameRulesContainer.appendChild(this._gameRules);
    }

    protected onPageClicked(): void {
        if (this._isOpen) {
            return;
        }

        this._isOpen = true;

        strictUiEmitter.emit('Game.command.round.uiDisabled', GameRules.NAME);

        if (this._needToLoad && !this._loaded) {
            this.load();
        } else if (this._loaded) {
            this.open();
        }
    }

    protected open(): void {
        this._gameRulesContainer && strictUiEmitter.emit('UI.gameRules.opened');

        strictUiEmitter.emit('Game.command.showInfo', {
            name: GameRules.NAME,
            content: this._gameRulesContainer,
            title: this._languageService.get('gameRules')
        });
    }

    protected close(): void {
        if (this._isOpen) {
            this._isOpen = false;
            this._gameRulesContainer && strictUiEmitter.emit('UI.gameRules.closed');
        }
    }

    protected load(): void {
        if (!this._needToLoad) {
            return;
        }

        const loader = this._resourceManager;
        const howToPlayText = 'howToPlay';
        const templates = {
            gameRules: '',
            howToPlay: ''
        };

        Promise.all([
            loader.loadHTMLTemplate('gameRules').then(template => {
                templates.gameRules = template;
            }),
            loader.loadTemplate(howToPlayText).then(template => {
                templates[howToPlayText] = template;
            }),
            this.getTextureFromCache('game_rules_AutoplayPopup'),
            this.generateLocalizedImages()
        ]).then(() => this.loadCallback(templates.gameRules, templates[howToPlayText]));

        this._needToLoad = false;
    }

    protected loadCallback(gameRulesTemplate: string, howToPlayTemplate: string): void {
        if (gameRulesTemplate && howToPlayTemplate) {
            this.insertHtml(gameRulesTemplate, this._gameRules);
            this.insertHtml(howToPlayTemplate, document.querySelector('#gameRules .howtoplay'));

            if (this.store.languageService.value!.get('translationsGameTerminology') === 'undefined') {
                document.getElementById('translationsGameTerminology')!.style.display = 'none';
            }

            (document.querySelector('#gameRules .close') as any).onclick = this.close.bind(this);

            this._loaded = true;
            this.open();
        } else {
            this._needToLoad = true;
        }
    }

    protected insertHtml(templateString: any, container: any): void {
        const config = this._gameConfig;
        const {currencyFormatter, languageService, logic_isWaysGame, logic_wager, logic_buyFeatureMultiplier} = this.store.values();
        const RTPFormatted = currencyFormatter!.formatNumber(config.RTP);
        const texts = {...languageService!.texts};
        const settings = this._settings.getAllProperties() as any;
        const isShowWinProbability = this._settings.get('showWinProbability');
        const isShowMaxMultiplier = this._settings.get('showMaxMultiplier');
        const detailedRTP: Record<'feature' | 'rtp', string>[] = [];

        if (!isShowWinProbability) {
            delete texts.winProbability;
        }

        let valuesForTexts: Record<string, any> = {
            returnToPlayerDescription: {RTP: RTPFormatted},
            winProbability: isShowWinProbability ? {winProbability: config.winProbability} : undefined,
            maxMultiplier: isShowMaxMultiplier ? {maxMultiplier: config.maxMultiplier} : undefined,
            clientVersion: {clientVersion: config.clientVersion},
            minBet: {minBet: currencyFormatter!.format(settings.minBet)},
            maxBet: {maxBet: currencyFormatter!.format(settings.maxBet)}
        };

        if (this._settings.get('buyFeatureAvailable') && config.buyFeatureConfig?.featureNameTranslationKey) {
            const featureName = languageService!.get(config.buyFeatureConfig?.featureNameTranslationKey);

            valuesForTexts = {
                ...valuesForTexts,
                buyFeatureDescription1: {featureName},
                buyFeatureDescription2: {
                    featureName,
                    featureMultiplier: logic_buyFeatureMultiplier
                },
                buyFeatureDescription7: {
                    featureRTP: currencyFormatter!.formatNumber(config.buyFeatureConfig?.RTP)
                }
            };
        }

        if (this._settings.get('showOddsUpfront')) {
            const {versions} = this._environmentProperty.buildProperty;
            // got from https://registry.rubyplay.tech/ui/repos/tree/General/maven-releases/rng-fortuna/rng-fortuna/1.0.1/rng-fortuna-1.0.1.jar
            const {RNGVersion = 'rng-fortuna-1.0.1.jar e0b1374f83ea1c643207ba0bbd80ed8463e3938f'} = settings;

            valuesForTexts = {...valuesForTexts,
                fileDate: {fileDate: new Date(versions.buildDate).toLocaleString()},
                serverVersion: {serverVersion: `${versions.math} ${this._gameConfig.mathHash}`},
                RNGVersion: {RNGVersion},
                betPredictionFormulaText: {wager: logic_wager},
                betPredictionFormulaTextWays: {wager: logic_wager}
            };
        } else {
            delete texts.fileDate;
            delete texts.serverVersion;
            delete texts.RNGVersion;
            delete texts.betPredictionFormulaText;
            delete texts.betPredictionFormulaTextWays;
        }

        if (settings.enableDetailedRTP) {
            for (const [key, val] of Object.entries(config.detailedRTP || {})) {
                const rtp = currencyFormatter!.formatNumber(val);

                detailedRTP.push({
                    feature:
                        languageService!.get(key) !== 'undefined'
                            ? languageService!.get(key)
                            : key,
                    rtp: `${rtp}%`
                });
            }
        }

        settings.showGameHistoryDescription = Boolean(this._settings.get('historyUrl'));

        for (const [textID, data] of Object.entries(valuesForTexts)) {
            if (languageService!.get(textID) !== 'undefined' && data !== undefined) {
                texts[textID] = languageService!.get(textID, data);
            }
        }

        const template = Hogan.compile(templateString);
        const lang = this._settings.get('lang');
        const images = this._templateUtils.createImageMap(templateString, lang, 1);

        const html = template.render({
            images,
            texts,
            detailedRTP,
            isWaysGame: logic_isWaysGame,
            ...settings,
            settings
        });

        container.insertAdjacentHTML('beforeend', html);
    }
}
